"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { API_CONFIG } from "@/config/api.config";
import { useLocale } from "@/i18n/LocaleProvider";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import { Button, Card, Field, Input } from "@/components/ui";
import Logo from "@/components/Logo";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { dict } = useLocale();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(API_CONFIG.routes.auth.login, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? dict.login.loginFailed);
        return;
      }
      router.replace(searchParams.get("from") || "/ui");
      router.refresh();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-accent-soft via-background to-background px-4">
      <Card className="w-full max-w-sm p-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 rounded-lg shadow-sm" />
            <h1 className="text-xl font-semibold text-foreground">
              {dict.appName}
            </h1>
          </div>
          <LocaleSwitcher />
        </div>
        <p className="mb-6 text-sm text-muted">{dict.login.subtitle}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label={dict.login.username}>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>

          <Field label={dict.login.password}>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          {error && (
            <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" loading={loading} className="w-full" size="lg">
            {loading ? dict.login.signingIn : dict.login.signIn}
          </Button>
        </form>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
